import React, { useMemo, useRef, useState } from 'react';
import { Box, Typography, IconButton, Grid, Snackbar, Fade, Divider } from '@material-ui/core';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import { useDispatch, useSelector } from 'react-redux';
import { _vc, setVC } from '../../../../redux/issueSlice';
import {
    _holderPrivateKey,
    _holderPrivateKeyEncoding,
    _vp,
    setHolderPrivateKey,
    setHolderPrivateKeyEncoding,
    setVP
} from '../../../../redux/presentSlice';
import { functions, verifiable } from 'sd-vc-lib';
import documentLoader from '../../../../utils/document-loader';
import { decodeBase64VC } from '../../../../utils/decode-vc';
import { WizardButton } from '../../../ui/WizardButton';
import FieldInput from '../../../ui/FeildInput';
import { OutputViewToggle } from '../../../ui/ActionBtns';
import CredentialClaimsPanel from './ui/CredentialClaimsPanel';
import { hexConverter } from '../../utils/hexConverter';
import { ViewMode } from '../../types/types';
import SectionDivider from '../../../ui/SectionDivider';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';
import ErrorIcon from '@material-ui/icons/Error';
import useClipboard from '../../../hooks/useClipboard';
import OutputPanel from '../../../ui/OutputPanel';
import { getSignErrorMessage } from '../../utils/getSignErrorMessage';

interface ExtraCred {
    parsed: any | null;
    claims: { key: string; value: any }[];
    masked: Record<string, boolean>;
}

function extractClaims(json: any): { key: string; value: any }[] {
    try {
        const subject = json?.credentialSubject ?? json?.vc?.credentialSubject ?? {};
        if (typeof subject !== 'object' || Array.isArray(subject)) return [];
        return Object.entries(subject).map(([key, value]) => ({ key, value }));
    } catch {
        return [];
    }
}

function buildMask(
    claims: { key: string; value: any }[],
    masked: Record<string, boolean>
): Record<string, boolean> {
    return Object.fromEntries(claims.filter((c) => !!masked[c.key]).map((c) => [c.key, true]));
}

export default function CredentialMask({ onMask }: { onMask?: () => void }) {
    const dispatch = useDispatch();
    const vc = useSelector(_vc);
    const vp = useSelector(_vp);
    const holderPrivateKey = useSelector(_holderPrivateKey);
    const holderPrivateKeyEncoding = useSelector(_holderPrivateKeyEncoding);

    const { snackBarState, copyToClipboard, handleClose } = useClipboard();

    const isFirstRender = useRef(true);

    const decodedVC = useMemo(() => decodeBase64VC(vc), [vc]);
    const isEmpty = !decodedVC || Object.keys(decodedVC).length === 0;

    const decodedVP = useMemo(() => {
        if (!vp || vp === 'Error!') return null;
        try {
            return decodeBase64VC(vp);
        } catch {
            return null;
        }
    }, [vp]);

    const claims = useMemo(() => extractClaims(decodedVC), [decodedVC]);
    const [checkedAttrs, setCheckedAttrs] = useState<Record<string, boolean>>({});
    const [viewMode, setViewMode] = useState<ViewMode>('encoded');

    React.useEffect(() => {
        setCheckedAttrs((prev) => {
            const claimKeys = claims.map((c) => c.key);
            const isSameSet =
                claimKeys.length === Object.keys(prev).length && claimKeys.every((k) => k in prev);
            if (isSameSet) return prev;
            const initial: Record<string, boolean> = {};
            claims.forEach((c) => (initial[c.key] = false));
            return initial;
        });
    }, [claims]);

    function toggleAttr(key: string) {
        setCheckedAttrs((prev) => ({ ...prev, [key]: !prev[key] }));
    }

    const [extraCreds, setExtraCreds] = useState<ExtraCred[]>([]);

    function addCredential() {
        setExtraCreds((prev) => [...prev, { parsed: null, claims: [], masked: {} }]);
    }

    function removeCredential(index: number) {
        setExtraCreds((prev) => prev.filter((_, i) => i !== index));
    }

    function loadClaimsFromParsed(index: number, parsed: any) {
        const claims = extractClaims(parsed);
        const masked: Record<string, boolean> = {};
        claims.forEach((c) => (masked[c.key] = false));
        setExtraCreds((prev) =>
            prev.map((ec, i) => (i === index ? { ...ec, parsed, claims, masked } : ec))
        );
    }

    function toggleExtraAttr(credIndex: number, key: string) {
        setExtraCreds((prev) =>
            prev.map((ec, i) =>
                i === credIndex ? { ...ec, masked: { ...ec.masked, [key]: !ec.masked[key] } } : ec
            )
        );
    }

    const canMask = !!(holderPrivateKey && vc);

    const [keyDIDWarning, setKeyDIDWarning] = useState<string | null>(null);
    const [maskError, setMaskError] = useState<string | null>(null);

    React.useEffect(() => {
        const expectedDID = (decodedVC as any)?.credentialSubject?.holder;
        if (!holderPrivateKey || !expectedDID) {
            setKeyDIDWarning(null);
            return;
        }
        const didMethod = expectedDID.split(':')[1] ?? 'key';
        try {
            const decodedKey = hexConverter(holderPrivateKey, holderPrivateKeyEncoding);
            (functions as any)
                .privateKeyToDoc(decodedKey, didMethod)
                .then((doc: any) => {
                    setKeyDIDWarning(doc.DID !== expectedDID ? doc.DID : null);
                })
                .catch(() => {
                    setKeyDIDWarning(null);
                });
        } catch {
            setKeyDIDWarning(null);
        }
    }, [holderPrivateKey, holderPrivateKeyEncoding, decodedVC]);

    React.useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        dispatch(setVP(''));
    }, [vc]);

    async function mask() {
        setMaskError(null);
        const loadedExtras = extraCreds.filter((ec) => ec.parsed !== null);
        const allCredentials = [
            JSON.parse(JSON.stringify(decodedVC)),
            ...loadedExtras.map((ec) => JSON.parse(JSON.stringify(ec.parsed)))
        ];
        const allMasks = [
            buildMask(claims, checkedAttrs),
            ...loadedExtras.map((ec) => buildMask(ec.claims, ec.masked))
        ];

        try {
            const decodedHolderPrivateKey = hexConverter(
                holderPrivateKey,
                holderPrivateKeyEncoding
            );
            const result = await verifiable.presentation.create({
                documentLoader,
                masks: allMasks,
                holderPrivateKey: decodedHolderPrivateKey,
                holderDID: (decodedVC as any)?.credentialSubject?.holder,
                verifiableCredential: allCredentials
            });
            dispatch(setVP(btoa(JSON.stringify(result))));
            onMask?.();
        } catch (e) {
            setMaskError(getSignErrorMessage(e, 'Mask'));
            dispatch(setVP(''));
        }
    }

    return (
        <div>
            <SectionDivider />
            <Box display="flex" flexDirection="column" style={{ gap: 20, width: '100%' }}>
                {/* PRIMARY CREDENTIAL */}
                <CredentialClaimsPanel
                    parsed={isEmpty ? null : decodedVC}
                    claims={claims}
                    masked={checkedAttrs}
                    onJsonEntered={(json) => dispatch(setVC(btoa(JSON.stringify(json))))}
                    onToggleClaim={toggleAttr}
                    onCopy={copyToClipboard}
                />

                {/* EXTRA CREDENTIALS */}
                {extraCreds.map((ec, index) => (
                    <Box
                        key={index}
                        style={{ border: '1px solid #e0e3e8', borderRadius: 8, padding: 16 }}>
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            style={{ marginBottom: 12 }}>
                            <Typography
                                style={{ fontWeight: 600, fontSize: '0.95rem', color: '#1a1a2e' }}>
                                Credential {index + 2}
                            </Typography>
                            <IconButton size="small" onClick={() => removeCredential(index)}>
                                <RemoveCircleOutlineIcon fontSize="small" />
                            </IconButton>
                        </Box>

                        <CredentialClaimsPanel
                            parsed={ec.parsed}
                            claims={ec.claims}
                            masked={ec.masked}
                            onJsonEntered={(json) => loadClaimsFromParsed(index, json)}
                            onToggleClaim={(key) => toggleExtraAttr(index, key)}
                            onCopy={copyToClipboard}
                        />
                    </Box>
                ))}

                <WizardButton
                    variant="outlined"
                    onClick={addCredential}
                    disabled={
                        extraCreds.length > 0 && extraCreds[extraCreds.length - 1].parsed === null
                    }
                    style={{ alignSelf: 'flex-start', fontSize: 13 }}>
                    Add more credentials
                </WizardButton>

                <Divider />

                <Box>
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        style={{ marginBottom: 6 }}>
                        <Typography style={{ fontWeight: 600 }}>Holder's private key</Typography>
                        <OutputViewToggle
                            tab={
                                holderPrivateKeyEncoding === 'Base58'
                                    ? 0
                                    : holderPrivateKeyEncoding === 'Base64'
                                      ? 1
                                      : 2
                            }
                            onTabChange={(idx) =>
                                dispatch(
                                    setHolderPrivateKeyEncoding(
                                        idx === 0 ? 'Base58' : idx === 1 ? 'Base64' : 'Hex'
                                    )
                                )
                            }
                            options={[{ label: 'Base58' }, { label: 'Base64' }, { label: 'Hex' }]}
                        />
                    </Box>
                    <FieldInput
                        value={holderPrivateKey || ''}
                        placeholder="Holder private key..."
                        onChange={(v) => dispatch(setHolderPrivateKey(v))}
                        multiline
                        rows={3}
                        style={{ width: '100%', border: '1px solid #1eb89a' }}
                    />
                    {keyDIDWarning && (
                        <Box
                            style={{
                                marginTop: 8,
                                padding: '10px 12px',
                                background: '#fffbf0',
                                border: '1px solid #ffe58f',
                                borderRadius: 8,
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 8
                            }}>
                            <WarningRoundedIcon
                                style={{
                                    fontSize: 16,
                                    color: '#faad14',
                                    flexShrink: 0,
                                    marginTop: 2
                                }}
                            />
                            <Box>
                                <Typography
                                    style={{ fontWeight: 400, color: '#7d5a00', lineHeight: 1.6 }}>
                                    This private key does not match the holder's DID in this
                                    credential. You may still continue for demo purposes
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Box>

                <WizardButton
                    variant="primary"
                    fullWidth
                    disabled={!canMask}
                    onClick={mask}
                    endIcon={<VisibilityOffIcon style={{ fontSize: '1.1rem' }} />}>
                    Next: Mask & Present
                </WizardButton>

                {/* MASK ERROR */}
                {maskError && (
                    <Box
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 12,
                            padding: 12,
                            borderRadius: 8,
                            backgroundColor: '#fff4f4',
                            border: '1px solid #ffcccc'
                        }}>
                        <Box style={{ display: 'flex', alignItems: 'center' }}>
                            <Typography style={{ fontWeight: 400, color: '#666' }}>
                                {maskError}
                            </Typography>
                        </Box>
                        <ErrorIcon style={{ color: '#d32f2f' }} />
                    </Box>
                )}

                {/* VP OUTPUT */}
                {vp && vp !== 'Error!' && (
                    <Grid item xs={12}>
                        <OutputPanel
                            title="Verifiable Presentation"
                            encodedValue={vp}
                            jsonValue={decodedVP}
                            viewMode={viewMode}
                            onViewModeChange={setViewMode}
                            onCopy={copyToClipboard}
                            maxHeight={480}
                        />
                    </Grid>
                )}

                <Snackbar
                    open={snackBarState.open}
                    onClose={handleClose}
                    TransitionComponent={Fade}
                    message={snackBarState.text}
                    autoHideDuration={5000}
                />
            </Box>
        </div>
    );
}
